/*  MUI  */
import { Cancel, CheckCircle, Edit } from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';

/*  Shared  */
import { Permission } from 'application/Shared/Components/Core';
import { PermissionPicker } from 'application/Shared/Components/Pickers';
import { FontSizes } from 'application/Shared/Constants';
import { Notify } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { Invitation } from 'application/Shared/types/invitation';
import { Permission as PermissionType } from 'application/Shared/types/permission';
import { User } from 'application/Shared/types/user';

/*  NPM  */
import axios from 'axios';
import { TFunction } from 'i18next';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const savePermission = async ({
    clientId,
    idToEdit,
    newPermissionId,
    permissionType,
    onSuccessFn,
    t
}: {
    clientId?: string;
    idToEdit: string;
    newPermissionId: string;
    onSuccessFn?: () => void;
    permissionType: string;
    t: TFunction<'translation', undefined>;
}) => {
    const params = { permissionId: newPermissionId } as {
        permissionId: string;
        platformTenantId?: string;
    };

    if (clientId) {
        params.platformTenantId = clientId;
    }

    try {
        if (permissionType === 'user') {
            await axios.put(`/user/${idToEdit}`, params);
        } else {
            await axios.put(`/invitation/${idToEdit}`, params);
        }

        Notify.s(t('User.permission.saved'));

        if (onSuccessFn) {
            onSuccessFn();
        }
    } catch (e) {
        debug.e(
            `Unable to save the ${
                permissionType === 'user' ? 'User' : 'Invite'
            } Permission.`,
            e
        );
        Notify.e(t('Common.error'));
    }
};

/**
 * Handles editing the permission for a user or invitation.
 * @param {Object} props
 * @param {User} props.user
 * @param {Invite} props.invite
 * @param {Function} [props.onSuccessFn]
 * @returns {JSX.Element}
 */
export const EditUserOrInvitePermission = ({
    user,
    invite,
    permissions,
    onSuccessFn = () => {}
}: {
    user?: User;
    invite?: Invitation;
    permissions: PermissionType[];
    onSuccessFn?: () => void;
}) => {
    const { t } = useTranslation();
    const { clientId } = useParams<{ clientId: string }>();
    const [modifyingPermission, setModifyingPermission] =
        useState<boolean>(false);
    const [newPermissionId, setNewPermissionId] = useState<string>('');

    const permissionType = user?.permissionId ? 'user' : 'invite';

    // Default to editing a user if we have one, otherwise use the invite id.
    const idToEdit = user?.id || invite?.id;

    const permissionId =
        newPermissionId || user?.permissionId || invite?.permissionId;

    // This should never happen since the row would not exist in the table, but just in case.
    if (!idToEdit) {
        return '-';
    }

    const onSuccess = () => {
        setModifyingPermission(false);
        setNewPermissionId('');
        if (onSuccessFn) {
            onSuccessFn();
        }
    };

    // Show the permission picker if we're currently editing the permission for this user
    if (modifyingPermission) {
        return (
            <Grid container>
                <Grid item xs={10}>
                    <PermissionPicker
                        permissionsData={permissions}
                        value={permissionId}
                        onChange={(v: string) => {
                            setNewPermissionId(v);
                        }}
                    />
                </Grid>
                <Grid item xs={2}>
                    <IconButton
                        data-trackid="manageUsers_permission_save"
                        disabled={!newPermissionId}
                        size="small"
                        onClick={() =>
                            savePermission({
                                clientId,
                                idToEdit,
                                newPermissionId,
                                onSuccessFn: onSuccess,
                                permissionType,
                                t
                            })
                        }
                        sx={{ color: 'icon.acceptSave' }}
                    >
                        <CheckCircle />
                    </IconButton>
                    <IconButton
                        data-trackid="manageUsers_permission_cancel"
                        size="small"
                        onClick={() => {
                            setModifyingPermission(false);
                            setNewPermissionId('');
                        }}
                        sx={{ color: 'icon.cancel' }}
                    >
                        <Cancel />
                    </IconButton>
                </Grid>
            </Grid>
        );
    }

    // Otherwise, show the visualOnly permission picker and edit button
    return (
        <>
            <PermissionPicker
                permissionsData={permissions}
                value={permissionId || ''}
                visualOnly
            />
            <IconButton
                data-trackid="manageUsers_permission_edit"
                size="small"
                color="secondary"
                onClick={() => setModifyingPermission(true)}
            >
                <Permission required="default_admin">
                    <Edit
                        style={{
                            fontSize: FontSizes[2]
                        }}
                    />
                </Permission>
            </IconButton>
        </>
    );
};
